import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import StudentQrUploadPanel from "../components/studentQr/StudentQrUploadPanel";
import { auth, db } from "../config/firebaseConfig";
import { uploadStudentQrImage } from "../services/storageService";
import {
  findStudentByUid,
  StudentQrError,
  upsertInactiveStudentQr,
} from "../services/studentService";
import {
  INVALID_ADMISSIONS_QR_FILENAME_MESSAGE,
  isQrImageFile,
  parseStudentUidFromAdmissionsFilename,
} from "../utils/studentQr";
import {
  getAllowedRoles,
  hasPermission,
  PERMISSIONS,
} from "../utils/roleAccess";

const DUPLICATE_QUEUE_MESSAGE =
  "Duplicate UID detected in queue. Last processed file will overwrite prior QR.";

function createQueueItem(file) {
  const imageFile = isQrImageFile(file);
  const uid = imageFile
    ? parseStudentUidFromAdmissionsFilename(file.name)
    : null;

  return {
    id: `${file.name}-${file.lastModified}-${Math.random()
      .toString(36)
      .slice(2, 9)}`,
    file,
    fileName: file.name,
    uid,
    progress: 0,
    resultType: "",
    status: imageFile && uid ? "pending" : "error",
    message: !imageFile
      ? "Only image files are supported for QR upload."
      : uid
        ? ""
        : INVALID_ADMISSIONS_QR_FILENAME_MESSAGE,
  };
}

function annotateDuplicateQueueEntries(items) {
  const uidCounts = items.reduce((accumulator, item) => {
    if (item.uid) {
      accumulator[item.uid] = (accumulator[item.uid] || 0) + 1;
    }
    return accumulator;
  }, {});

  return items.map((item) => {
    if (!item.uid || uidCounts[item.uid] < 2) {
      if (item.message === DUPLICATE_QUEUE_MESSAGE) {
        return { ...item, message: "" };
      }
      return item;
    }

    return {
      ...item,
      message:
        item.status === "error" && item.message
          ? item.message
          : DUPLICATE_QUEUE_MESSAGE,
    };
  });
}

function getReadableErrorMessage(error) {
  if (error instanceof StudentQrError) {
    switch (error.code) {
      case "invalid-uid":
        return "Could not read UID from filename. Please check file format.";
      case "missing-qr-image-url":
        return "Missing QR image URL after upload.";
      default:
        return error.message;
    }
  }

  switch (error?.code) {
    case "storage/object-not-found":
      return "QR image not found in Firebase Storage.";
    case "storage/unauthorized":
      return "You do not have permission to access Firebase Storage.";
    default:
      return error?.message || "Unexpected Firebase error. Please try again.";
  }
}

function createSummaryCards(queue) {
  const totalQueued = queue.length;
  const pending = queue.filter((item) => item.status === "pending").length;
  const created = queue.filter(
    (item) => item.status === "success" && item.resultType === "created",
  ).length;
  const updated = queue.filter(
    (item) => item.status === "success" && item.resultType === "updated",
  ).length;
  const errors = queue.filter((item) => item.status === "error").length;

  return [
    {
      label: "Files in queue",
      value: totalQueued,
      icon: <Inventory2OutlinedIcon color="primary" />,
    },
    {
      label: "Pending upload",
      value: pending,
      icon: <SyncRoundedIcon color="info" />,
    },
    {
      label: "Student docs created",
      value: created,
      icon: <PersonAddAlt1OutlinedIcon color="success" />,
    },
    {
      label: "Updated / errors",
      value: `${updated} / ${errors}`,
      icon: <WarningAmberRoundedIcon color="warning" />,
    },
  ];
}

export default function AdmissionsQrBulkUpload() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [user, setUser] = useState(null);
  const [canManageQr, setCanManageQr] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [queue, setQueue] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [overwriteExisting, setOverwriteExisting] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const [academicYear, setAcademicYear] = useState("2026-27");
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const showSnackbar = useCallback((severity, message) => {
    setSnackbarState({
      open: true,
      severity,
      message,
    });
  }, []);

  const updateQueueItem = useCallback((queueItemId, updates) => {
    setQueue((currentItems) =>
      currentItems.map((item) =>
        item.id === queueItemId ? { ...item, ...updates } : item,
      ),
    );
  }, []);

  useEffect(() => {
    let active = true;

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!active) {
        return;
      }

      try {
        if (!currentUser) {
          setUser(null);
          setCanManageQr(false);
          return;
        }

        setUser(currentUser);
        const userReference = doc(db, "users", currentUser.uid);
        const userSnapshot = await getDoc(userReference);

        if (!active) {
          return;
        }

        setCanManageQr(
          userSnapshot.exists() &&
            hasPermission(userSnapshot.data(), PERMISSIONS.STUDENT_QR_MANAGER),
        );
      } catch (error) {
        if (active) {
          showSnackbar("error", getReadableErrorMessage(error));
          setCanManageQr(false);
        }
      } finally {
        if (active) {
          setAuthLoading(false);
        }
      }
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [showSnackbar]);

  const summaryCards = useMemo(() => createSummaryCards(queue), [queue]);

  const handleQueueFiles = useCallback(
    (fileList) => {
      const files = Array.from(fileList || []);

      if (!files.length) {
        return;
      }

      const newQueueItems = files.map(createQueueItem);

      setQueue((currentItems) =>
        annotateDuplicateQueueEntries([...currentItems, ...newQueueItems]),
      );

      showSnackbar(
        "success",
        `${files.length} file(s) added to the upload queue.`,
      );
    },
    [showSnackbar],
  );

  const handleBulkUpload = useCallback(async () => {
    const pendingItems = queue.filter((item) => item.status === "pending");

    if (!pendingItems.length) {
      showSnackbar("warning", "No pending admissions QR uploads in the queue.");
      return;
    }

    setBulkUploading(true);
    let successCount = 0;
    let skippedCount = 0;
    let errorCount = 0;
    let createdCount = 0;
    let updatedCount = 0;

    for (const item of pendingItems) {
      try {
        updateQueueItem(item.id, {
          status: "uploading",
          progress: 0,
          message: item.message === DUPLICATE_QUEUE_MESSAGE ? item.message : "",
        });

        const existingStudent = await findStudentByUid(item.uid);

        if (existingStudent?.qrImageUrl && !overwriteExisting) {
          skippedCount += 1;
          updateQueueItem(item.id, {
            status: "skipped",
            progress: 0,
            resultType: "",
            message:
              "QR image already exists for this UID. Enable overwrite to replace it.",
          });
          continue;
        }

        const qrImageUrl = await uploadStudentQrImage({
          uid: item.uid,
          file: item.file,
          onProgress: (progress) => {
            updateQueueItem(item.id, { progress });
          },
        });

        const result = await upsertInactiveStudentQr({
          uid: item.uid,
          qrImageUrl,
          academicYear,
          existingStudent,
        });

        successCount += 1;
        if (result.created) {
          createdCount += 1;
        } else {
          updatedCount += 1;
        }

        updateQueueItem(item.id, {
          status: "success",
          progress: 100,
          resultType: result.created ? "created" : "updated",
          message: result.created
            ? `Created new student doc for UID ${item.uid} with empty fields. activeqr set to false.`
            : `Updated UID ${item.uid}. QR linked and activeqr set to false.`,
        });
      } catch (error) {
        errorCount += 1;
        updateQueueItem(item.id, {
          status: "error",
          progress: 0,
          resultType: "",
          message: getReadableErrorMessage(error),
        });
      }
    }

    setBulkUploading(false);

    showSnackbar(
      errorCount > 0 ? "warning" : "success",
      `Admissions QR sync finished. Success: ${successCount}, Created: ${createdCount}, Updated: ${updatedCount}, Skipped: ${skippedCount}, Errors: ${errorCount}.`,
    );
  }, [academicYear, overwriteExisting, queue, showSnackbar, updateQueueItem]);

  const handleDragEnter = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
  }, []);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      setDragActive(false);
      handleQueueFiles(event.dataTransfer.files);
    },
    [handleQueueFiles],
  );

  if (authLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "grey.50",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CircularProgress size={24} />
          <Typography color="text.secondary">
            Loading admissions QR page...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (!user || !canManageQr) {
    const allowedRoles = getAllowedRoles(PERMISSIONS.STUDENT_QR_MANAGER).join(
      ", ",
    );

    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "grey.50",
          px: 2,
        }}
      >
        <Card sx={{ maxWidth: 520, borderRadius: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" fontWeight={700}>
              Access required
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mt: 1.5, mb: 3 }}
            >
              This admissions QR bulk uploader can write to Firebase Firestore
              and Storage. Allowed roles: {allowedRoles || "admin"}.
            </Typography>
            <Button variant="contained" onClick={() => navigate("/login")}>
              Login
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f4f7fb", py: { xs: 3, md: 4 } }}>
      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
          >
            <Box>
              <Typography variant="h4" fontWeight={800}>
                Admissions QR Bulk Upload
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                Upload all admission QR images in one run. The first 6
                characters of every filename are treated as the student UID and
                records are saved in `students` with `activeqr = false`.
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<ArrowBackRoundedIcon />}
              onClick={() => navigate("/admin-dashboard")}
              sx={{ borderRadius: 999 }}
            >
              Back to Dashboard
            </Button>
          </Stack>

          <Card sx={{ borderRadius: 4, boxShadow: "none" }}>
            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", md: "center" }}
              >
                <TextField
                  label="Academic year"
                  value={academicYear}
                  onChange={(event) => setAcademicYear(event.target.value)}
                  size="small"
                  sx={{ maxWidth: 220 }}
                />
                <Typography variant="body2" color="text.secondary">
                  Example filename format: `26X00A+student_qr_logo.png` {" -> "}{" "}
                  UID `26X00A`. Missing students are auto-created with empty
                  dummy fields.
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          <Grid container spacing={2.5}>
            {summaryCards.map((card) => (
              <Grid item xs={12} sm={6} lg={3} key={card.label}>
                <Card sx={{ borderRadius: 4, boxShadow: "none" }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {card.label}
                        </Typography>
                        <Typography
                          variant="h4"
                          fontWeight={800}
                          sx={{ mt: 1 }}
                        >
                          {card.value}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          display: "grid",
                          placeItems: "center",
                          borderRadius: 3,
                          bgcolor: "grey.100",
                        }}
                      >
                        {card.icon}
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <StudentQrUploadPanel
            bulkUploading={bulkUploading}
            description="Drag and drop admission QR images or browse from your machine. The first 6 characters of each filename are used as UID."
            dragActive={dragActive}
            dropDescription="Supports multi-file upload. During upload, missing students are auto-created with empty fields and inactive QR status."
            dropTitle="Drop admissions QR images here"
            fileInputRef={fileInputRef}
            overwriteExisting={overwriteExisting}
            queue={queue}
            title="Admissions Bulk QR Queue"
            onOpenFilePicker={() => fileInputRef.current?.click()}
            onFilesSelected={handleQueueFiles}
            onUploadPending={handleBulkUpload}
            onClearQueue={() => setQueue([])}
            onRemoveQueueItem={(queueItemId) =>
              setQueue((currentItems) =>
                annotateDuplicateQueueEntries(
                  currentItems.filter((item) => item.id !== queueItemId),
                ),
              )
            }
            onToggleOverwriteExisting={setOverwriteExisting}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          />
        </Stack>
      </Container>

      <Snackbar
        open={snackbarState.open}
        autoHideDuration={5000}
        onClose={(_, reason) => {
          if (reason === "clickaway") {
            return;
          }
          setSnackbarState((currentState) => ({
            ...currentState,
            open: false,
          }));
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbarState.severity} variant="filled">
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
